import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Plus } from "lucide-react";
import { fromPrice, inr, media } from "@/lib/format";
import { useKiosk } from "./KioskContext";
import { specRows } from "./specs";

const Picker = ({ models, compare, toggle }) => (
  <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="compare-picker">
    {models.map((m) => {
      const on = compare.includes(m.slug);
      return (
        <button key={m.slug} onClick={() => toggle(m.slug)} data-testid={`compare-pick-${m.slug}`}
          className={`flex min-h-[52px] shrink-0 items-center gap-2 rounded-full pl-2 pr-4 text-[15px] font-medium transition-colors ${on ? "bg-[#1D1D1F] text-white" : "bg-[#F5F5F7]"}`}>
          <img src={media(m.hero_image)} alt="" className="h-9 w-12 object-contain" />{m.short}{on ? <X size={16} /> : <Plus size={16} />}
        </button>
      );
    })}
  </div>
);

export default function Compare() {
  const { data, t, compare, setCompare, logEvent } = useKiosk();
  const models = data.models;
  useEffect(() => { if (!compare.length) setCompare(models.slice(0, 2).map((m) => m.slug)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (slug) => { if (!compare.includes(slug)) logEvent("compare", slug); setCompare((c) => (c.includes(slug) ? c.filter((x) => x !== slug) : [...c, slug].slice(-3))); };
  const picked = compare.map((s) => models.find((m) => m.slug === s)).filter(Boolean);
  const rows = picked.length ? specRows(picked[0], t).map((r) => r.key) : [];

  return (
    <main className="mx-auto max-w-[1280px] px-6 pb-12 lg:px-10" data-testid="compare-page">
      <h1 className="font-display pt-4 text-5xl lg:text-[64px] font-extrabold tracking-[-0.045em]">{t("compare")}</h1>
      <p className="mb-5 mt-1 text-lg text-[#6E6E73]">{t("pickModels")}</p>
      <Picker models={models} compare={compare} toggle={toggle} />
      <div className="mt-6 grid gap-5" style={{ gridTemplateColumns: `repeat(${Math.max(picked.length, 1)}, minmax(0, 1fr))` }}>
        {picked.map((m) => {
          const specs = specRows(m, t);
          return (
            <div key={m.slug} className="tile flex flex-col p-6" data-testid={`compare-col-${m.slug}`}>
              <div className="relative h-[180px]">
                <div className="floor-glow absolute bottom-0 left-1/2 h-14 w-[70%] -translate-x-1/2" />
                <img src={media(m.hero_image)} alt={m.name} className="product-shadow relative h-full w-full object-contain" />
              </div>
              <p className="mt-4 text-[14px] font-semibold text-[#087568]">{m.category}</p>
              <h2 className="font-display text-3xl font-extrabold tracking-[-0.03em]">{m.short}</h2>
              <p className="mt-1 text-[15px]"><span className="font-semibold tnum">{t("from")} {inr(fromPrice(m))}</span></p>
              <p className="text-[12px] text-[#6E6E73]">{t("exShowroom")} · {t("indicative")}</p>
              <dl className="mt-4 divide-y divide-[#E5E5EA]">
                {rows.map((key) => {
                  const r = specs.find((x) => x.key === key);
                  return (
                    <div key={key} className="flex justify-between gap-3 py-3 text-[15px]">
                      <dt className="text-[#6E6E73]">{r.label}</dt><dd className="text-right font-semibold tnum">{r.value}</dd>
                    </div>
                  );
                })}
              </dl>
              <div className="mt-4 flex gap-2">
                <Link to={`/kiosk/model/${m.slug}`} className="btn-pill btn-ghost flex-1 !px-4" data-testid={`compare-explore-${m.slug}`}>{t("explore")}</Link>
                <Link to={`/kiosk/book/quote?model=${m.slug}`} className="btn-pill btn-teal flex-1 !px-4" data-testid={`compare-quote-${m.slug}`}>{t("getQuote")}</Link>
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}
